/**
 * EditableText — a contentEditable span with React-friendly semantics.
 *
 * Designed to feel like editing a Word document:
 *   - No visible edit affordances when idle
 *   - Subtle hover background to hint editability
 *   - Cursor appears on click; no input border or focus ring
 *
 * React + contentEditable needs careful state sync: if React re-renders with
 * new children while the user is typing, the cursor jumps. This component
 * treats the DOM as source of truth during editing and syncs from props only
 * when not focused.
 */
import { useEffect, useRef, type KeyboardEvent } from 'react';

interface Props {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  className?: string;
  multiline?: boolean;
  ariaLabel?: string;
  /** Fired when user presses Enter (non-shift) — lets parent handle as "new sibling" */
  onEnter?: () => void;
  /** Fired when user presses Backspace on empty content — lets parent delete the node */
  onDeleteEmpty?: () => void;
}

export function EditableText({
  value,
  onChange,
  placeholder,
  className,
  multiline = false,
  ariaLabel,
  onEnter,
  onDeleteEmpty,
}: Props) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    if (document.activeElement !== ref.current && ref.current.textContent !== value) {
      ref.current.textContent = value;
    }
  }, [value]);

  useEffect(() => {
    if (ref.current && ref.current.textContent !== value) {
      ref.current.textContent = value;
    }
  }, []);

  function handleBlur() {
    const current = ref.current?.textContent ?? '';
    if (current !== value) {
      onChange(current);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLSpanElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      if (!multiline) {
        e.preventDefault();
        ref.current?.blur();
        onEnter?.();
      }
    }
    if (
      e.key === 'Backspace' &&
      !ref.current?.textContent &&
      onDeleteEmpty &&
      window.getSelection()?.toString() === ''
    ) {
      e.preventDefault();
      onDeleteEmpty();
    }
  }

  return (
    <span
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      aria-label={ariaLabel}
      data-placeholder={placeholder}
      // Discoverable editability:
      //  - Idle: invisible (reads as regular document text).
      //  - Hover (when not focused): pine-tinted bg + dashed pine
      //    underline so "click to edit" is obvious without relying
      //    on muscle memory.
      //  - Focus: pine-tinted bg only (cursor + typing signal
      //    editability; underline would fight with the text).
      //  - Empty: italic placeholder in smoke.
      className={
        'inline cursor-text outline-none rounded-sm px-0.5 -mx-0.5 ' +
        'transition-colors ' +
        '[&:hover:not(:focus)]:bg-pine/10 ' +
        '[&:hover:not(:focus)]:underline ' +
        '[&:hover:not(:focus)]:decoration-dashed ' +
        '[&:hover:not(:focus)]:decoration-pine/45 ' +
        '[&:hover:not(:focus)]:underline-offset-[3px] ' +
        'focus:bg-pine/10 focus:no-underline ' +
        'empty:before:content-[attr(data-placeholder)] empty:before:text-smoke/50 empty:before:italic ' +
        (className ?? '')
      }
      style={{ whiteSpace: multiline ? 'pre-wrap' : 'normal' }}
    />
  );
}
